import type { RealtimeClient } from '@openai/realtime-api-beta';

type SetFn<T = any> = (value: T | ((prev: T) => T)) => void;

interface RefLike<T> {
  current: T;
}

interface RegisterManageListingDeps {
  listingPayloadRef: RefLike<any>;
  listingFlowSessionIdRef: RefLike<number>;
  setListingPayload: SetFn<any>;
  setCurrentListingQuestion: SetFn<string | null>;
  setImageUploadModalOpen: (v: boolean) => void;
  setPayloadPreviewOpen: (v: boolean) => void;
  setAreaSelectionModalOpen: (v: boolean) => void;
  setAreaOptions: SetFn<any[]>;
  setAreaSearchQuery: SetFn<string>;
}

export function registerManagePropertyListingTool(
    client: RealtimeClient,
    {
      listingPayloadRef,
      listingFlowSessionIdRef,
      setListingPayload,
      setCurrentListingQuestion,
      setImageUploadModalOpen,
      setPayloadPreviewOpen,
      setAreaSelectionModalOpen,
      setAreaOptions,
      setAreaSearchQuery,
    }: RegisterManageListingDeps
) {
  // Helpers remain the same
  const normalize = (s?: string) => (s || '').trim().toLowerCase();

  const getCityId = async (name: string): Promise<number | null> => {
    if (!name) return null;
    try {
      const citiesLocal = [
        { id: 1, name: 'Islamabad' },
        { id: 169, name: 'Karachi' },
        { id: 2, name: 'Lahore' },
        { id: 176, name: 'Peshawar' },
        { id: 3, name: 'Rawalpindi' },
      ];
      const local = citiesLocal.find((c) => normalize(c.name) === normalize(name))?.id || null;
      if (local) return local;

      const res = await fetch('https://www.graana.com/data/cities');
      const json = await res.json();
      return json?.data?.find((c: any) => normalize(c.name) === normalize(name))?.id ?? null;
    } catch (e) {
      console.error('city resolve failed', e);
      return null;
    }
  };

  const searchArea = async (cityId: number, area: string) => {
    try {
      const endpoint = `https://www.graana.com/search/cityArea?cityId=${cityId}&searchQuery=${encodeURIComponent(area)}`;
      const res = await fetch(endpoint);
      const json = await res.json();
      const arr = (json?.data || []).filter((a: any) => a.areaId);

      if (!arr || arr.length === 0) {
        return { areaId: null, coords: undefined, areas: [], shouldShowSelection: false };
      }

      const q = normalize(area);
      const exact = arr.find((a: any) => normalize(a.name) === q);
      if (exact) {
        const lat = exact.lat ?? exact.latitude ?? exact?.location?.lat;
        const lng = exact.lng ?? exact.longitude ?? exact?.location?.lng;
        const coords = typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : undefined;
        return { areaId: exact.areaId, coords, areas: [exact], shouldShowSelection: false };
      }

      if (arr.length > 1) {
        return { areaId: null, coords: undefined, areas: arr.slice(0, 25), shouldShowSelection: true };
      }

      const pick = arr[0];
      const lat = pick.lat ?? pick.latitude ?? pick?.location?.lat;
      const lng = pick.lng ?? pick.longitude ?? pick?.location?.lng;
      const coords = typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : undefined;
      return { areaId: pick.areaId, coords, areas: [pick], shouldShowSelection: false };
    } catch (e) {
      console.error('area resolve failed', e);
      return { areaId: null, coords: undefined, areas: [], shouldShowSelection: false };
    }
  };

  // FIX: client.addTool takes two arguments: the definition and the implementation function.
  client.addTool(
      {
        name: 'upload_property_listing',
        description: 'Manages the property listing process. Use it to collect and update information. It intelligently validates and asks for missing data. When all required information is gathered, call it one last time with all the data and submit: true to finalize the listing.',
        parameters: {
          type: 'object',
          properties: {
            purpose: { type: 'string', enum: ['rent', 'buy'] },
            city: { type: 'string' },
            area: { type: 'string' },
            bed: { type: 'number' },
            bath: { type: 'number' },
            size: { type: 'number' },
            sizeUnit: { type: 'string', enum: ['sqm', 'sqyd', 'marla', 'kanal', 'sqft'] },
            price: { type: 'number' },
            submit: { type: 'boolean' },
            nextQuestion: { type: 'string', description: "DEPRECATED: This field is now ignored. The tool determines the next question automatically." },
          },
        },
      },
      // Argument 2: Tool Implementation Function
      async (params: any) => {
        console.log('Received params:', params);
        console.log('--- TOOL CALLED ---');
        console.log('Parameters received by the tool:', JSON.stringify(params, null, 2));

        const { submit, ...restParams } = params;

        if (submit) {
          const currentPayload = listingPayloadRef.current;
          const currentImageIds = currentPayload?.imageIds || [];
          const imageObjects = currentImageIds.map((id: string, index: number) => ({ type: 'cover', order: index + 1, id }));
          const cityId = currentPayload?.cityId;
          if (!currentPayload?.cityId || !currentPayload?.areaId || !currentPayload?.price || !currentPayload?.imageIds?.length) {
            return "معاف کیجئے، جمع کرانے سے پہلے تمام ضروری معلومات (شہر، علاقہ، قیمت، اور تصاویر) فراہم کرنا ضروری ہیں۔";
          }

          const hardcodedFields = {
            imageIds: imageObjects,
            propertySource: 'graana',
            cityId,
            name: 'Furqan Ahmad',
            phone: '3009652778',
            areaId: currentPayload?.areaId,
            userId: '1f73ab3e-b43f-414a-aa44-a7ceecfb3974',
            countryCode: '+92',
            subtype: 'house',
            type: 'residential',
            areaCoordinates: currentPayload?.areaCoordinates,
            condition: 'Brand new',
          };

          const { city: _omitCity, area: _omitArea, nextQuestion: _nq, submit: _s, ...aiPayloadWithoutLocation } = params || {};

          const merged = {
            purpose: params?.purpose ?? currentPayload?.purpose,
            size: params?.size ?? currentPayload?.size,
            sizeUnit: params?.sizeUnit ?? currentPayload?.sizeUnit,
            price: params?.price ?? currentPayload?.price,
            bed: params?.bed ?? currentPayload?.bed,
            bath: params?.bath ?? currentPayload?.bath,
          };

          const toNum = (v: any) => v === undefined || v === null || v === '' ? undefined : Number(v);

          const finalPayload = {
            ...hardcodedFields,
            ...aiPayloadWithoutLocation,
            purpose: merged.purpose,
            size: toNum(merged.size),
            price: toNum(merged.price),
            bed: toNum(merged.bed),
            bath: toNum(merged.bath),
            sizeUnit: merged.sizeUnit ?? undefined,
            customTitle: merged.size && merged.sizeUnit && merged.purpose
                ? `${merged.size} ${merged.sizeUnit} house for ${merged.purpose === 'buy' ? 'sale' : 'rent'} in ${currentPayload?.city}`
                : `house for ${merged.purpose === 'buy' ? 'sale' : 'rent'} in ${currentPayload?.city}`,
            imageIds: imageObjects,
          };

          try {
            const response = await fetch('https://www.graana.com/data/property', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(finalPayload),
            });
            const result = await response.json();
            console.log('Graana API Response:', result);

            // setListingPayload(null);
            setCurrentListingQuestion(null);
            // setPayloadPreviewOpen(false);

            if (response.ok) {
              return 'آپ کا ڈیٹا کامیابی سے جمع کر لیا گیا ہے۔ شکریہ۔';
            } else {
              return `معاف کیجئے، کچھ خرابی آ گئی ہے۔ براہ کرم دوبارہ کوشش کریں۔ سرور کا جواب: ${result.message || 'نامعلوم خرابی'}`;
            }
          } catch (error) {
            console.error('Error submitting property listing:', error);
            setListingPayload(null);
            setCurrentListingQuestion(null);
            setPayloadPreviewOpen(false);
            return 'معاف کیجئے، کچھ خرابی آ گئی ہے۔ براہ کرم دوبارہ کوشش کریں۔';
          }
        }

        const currentPayload = listingPayloadRef.current || {};
        let updatedPayload = { ...currentPayload };
        for (const key in restParams) {
          const value = restParams[key];
          if (value !== undefined && value !== null && value !== '') {
            updatedPayload[key] = value;
          }
        }

        if (params.purpose && !currentPayload.purpose) {
          listingFlowSessionIdRef.current++;
          // setPayloadPreviewOpen(true);
        }

        // CHANGE 2: A clear, sequential, and prioritized gap analysis.
        // This makes the flow predictable and reliable.

        // Priority 1: Purpose
        if (!updatedPayload.purpose) {
          setCurrentListingQuestion('purpose');
          setListingPayload(updatedPayload);
          return "کیا آپ پراپرٹی بیچنا چاہتے ہیں یا کرایہ پر دینا چاہتے ہیں؟ ('buy' or 'rent')";
        }

        if (!updatedPayload.cityId) {
          if (!updatedPayload.city) {
            setCurrentListingQuestion('city');
            setListingPayload(updatedPayload);
            return "آپ کی پراپرٹی کس شہر میں ہے؟";
          }
          const resolvedCityId = await getCityId(updatedPayload.city);
          if (!resolvedCityId) {
            delete updatedPayload.city; // Invalidate the wrong city
            setCurrentListingQuestion('city');
            setListingPayload(updatedPayload);
            return 'معاف کیجئے، یہ شہر شناخت نہیں ہو سکا۔ براہ کرم درست شہر بتائیں (مثلاً لاہور، کراچی)۔';
          }
          updatedPayload.cityId = resolvedCityId;
        }

        // Priority 3: Area
        if (!updatedPayload.areaId) {
          if (!updatedPayload.area) {
            setCurrentListingQuestion('area');
            setListingPayload(updatedPayload);
            return `یہ ${updatedPayload.city} کے کس علاقے میں ہے؟`;
          }
          const areaResult = await searchArea(updatedPayload.cityId, updatedPayload.area);
          if (areaResult.shouldShowSelection) {
            setAreaOptions(areaResult.areas);
            setAreaSearchQuery(updatedPayload.area);
            setAreaSelectionModalOpen(true);
            setCurrentListingQuestion(null);
            setListingPayload(updatedPayload);
            return `"${updatedPayload.area}" کے لیے متعدد علاقے ملے ہیں۔ براہ کرم اپنا مطلوبہ علاقہ منتخب کریں۔`;
          }
          if (areaResult.areaId) {
            updatedPayload.area = areaResult.areas[0].name;
            updatedPayload.areaId = areaResult.areaId;
            updatedPayload.areaCoordinates = areaResult.coords;
          } else {
            delete updatedPayload.area; // Invalidate the wrong area
            setCurrentListingQuestion('area');
            setListingPayload(updatedPayload);
            return `معاف کیجئے، "${params.area}" کے نام سے کوئی علاقہ نہیں ملا۔ براہ کرم کوئی اور علاقہ بتائیں۔`;
          }
        }

        // Priority 4: Bed
        if (updatedPayload.bed === undefined) {
          setCurrentListingQuestion('bed');
          setListingPayload(updatedPayload);
          return "اس میں کتنے بیڈروم ہیں؟";
        }

        // Priority 5: Bath
        if (updatedPayload.bath === undefined) {
          setCurrentListingQuestion('bath');
          setListingPayload(updatedPayload);
          return "اس میں کتنے واش روم ہیں؟";
        }

        // Priority 6: Size
        if (updatedPayload.size === undefined || updatedPayload.sizeUnit === undefined) {
          setCurrentListingQuestion('size');
          setListingPayload(updatedPayload);
          return "پراپرٹی کا سائز کتنا ہے؟ (مثال: 5 مرلہ، 2 کنال)";
        }

        // Priority 7: Price
        if (updatedPayload.price === undefined) {
          setCurrentListingQuestion('price');
          setListingPayload(updatedPayload);
          return "آپ کی مطلوبہ قیمت کیا ہے؟";
        }

        // Priority 8: Images
        if (!updatedPayload.imageIds || updatedPayload.imageIds.length === 0) {
          setCurrentListingQuestion('image');
          setImageUploadModalOpen(true);
          setListingPayload(updatedPayload);
          return "تمام تفصیلات مکمل ہو گئی ہیں۔ براہ کرم اپنے گھر کی سامنے والی تصویر اپلوڈ کریں۔";
        }

        setListingPayload(updatedPayload);
        setCurrentListingQuestion(null);
        return "تمام معلومات جمع ہو چکی ہیں۔ براہ کرم `submit: true` کے ساتھ فائنل کال کریں تاکہ لسٹنگ پوسٹ کی جا سکے۔";
      }
  );
}